import { useEffect, useMemo, useState } from "react";
import { api, cachedGet } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { formatApiError } from "@/lib/apiClient";
import { X, Zap, Clock, CheckCircle2 } from "lucide-react";

/**
 * DailyReportPopup — quick capture modal shown to consultants/assistants at login when:
 *   - they haven't reported the numbers for TODAY yet, AND
 *   - it's past 16:00 local time (end-of-day nudge).
 *
 * Dispensable for 4h via localStorage.
 * Writes via POST /daily-entries → aggregates automatically into weekly_entries.
 */

const FIELDS = [
  { key: "contactos", label: "Contactos", short: "Contactos" },
  { key: "reunioes", label: "Reuniões", short: "Reuniões" },
  { key: "angariacoes", label: "Angariações", short: "Angar." },
  { key: "vendas", label: "Vendas", short: "Vendas" },
  { key: "social_posts", label: "Posts Sociais", short: "Posts" },
  { key: "humanized_videos", label: "Vídeos Humanizados", short: "Vídeos" },
  { key: "formacoes", label: "Formações", short: "Formações" },
];

const DISMISS_KEY = "kw_daily_popup_dismissed_until";
const DISMISS_MS = 4 * 60 * 60 * 1000; // 4h

function isoDate(offset = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function shortLabel(iso) {
  try {
    const d = new Date(iso + "T12:00:00");
    return d.toLocaleDateString("pt-PT", { weekday: "short", day: "2-digit", month: "2-digit" });
  } catch { return iso; }
}

export default function DailyReportPopup() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [dayOffset, setDayOffset] = useState(0); // 0 = hoje, -1 = ontem
  const [values, setValues] = useState({});
  const [playerId, setPlayerId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [checked, setChecked] = useState(false);

  const today = useMemo(() => isoDate(0), []);
  const yesterday = useMemo(() => isoDate(-1), []);
  const targetDate = dayOffset === 0 ? today : yesterday;

  // Gate — only consultants/assistants, not admin/coach
  const eligible = user && (user.role === "consultant" || user.role === "staff");

  useEffect(() => {
    if (!eligible || checked) return;
    // Time-of-day gate — only after 16:00 local
    const now = new Date();
    if (now.getHours() < 16) { setChecked(true); return; }
    // Dispense gate — respect 4h dismiss
    const dismissedUntil = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (dismissedUntil > Date.now()) { setChecked(true); return; }
    (async () => {
      try {
        const stats = (await api.get("/my-stats")).data;
        const p = stats?.player;
        if (!p?.id) { setChecked(true); return; }
        setPlayerId(p.id);
        // Fetch today's daily entry to check if already reported
        const week = (await cachedGet("/settings")).data.current_week_computed || 1;
        const dr = (await api.get(`/daily-entries?week=${week}`)).data;
        const todaysEntry = (dr.entries || []).find((e) => e.date === today);
        const alreadyReported = !!todaysEntry && FIELDS.some((f) => (todaysEntry[f.key] || 0) > 0);
        if (alreadyReported) { setChecked(true); return; }
        // Seed values from existing entry (if partially filled) or zeros
        const seed = {};
        FIELDS.forEach((f) => { seed[f.key] = (todaysEntry && todaysEntry[f.key]) || 0; });
        setValues(seed);
        setOpen(true);
      } catch { /* silent */ }
      setChecked(true);
    })();
  }, [eligible, checked, today]);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_MS));
    setOpen(false);
  };

  const save = async () => {
    if (!playerId) return;
    setSaving(true);
    try {
      const payload = { player_id: playerId, date: targetDate };
      FIELDS.forEach((f) => { payload[f.key] = Number(values[f.key] || 0); });
      await api.post("/daily-entries", payload);
      const total = FIELDS.reduce((sum, f) => sum + Number(values[f.key] || 0), 0);
      toast.success(`${total} actividades registadas para ${shortLabel(targetDate)}. Bom trabalho!`);
      setOpen(false);
    } catch (err) { toast.error(formatApiError(err)); }
    setSaving(false);
  };

  const setField = (k, v) => setValues((prev) => ({ ...prev, [k]: Math.max(0, Number(v) || 0) }));

  // When the popup opens, force the page to top and lock body scroll behind it.
  // Prevents the modal appearing far below the fold on tall dashboards.
  useEffect(() => {
    if (!open) return;
    window.scrollTo({ top: 0, behavior: "instant" });
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = originalOverflow; };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center p-3 pt-6 sm:pt-16 overflow-y-auto"
      data-testid="daily-report-popup"
    >
      <div className="kw-card w-full max-w-lg relative max-h-[calc(100vh-2rem)] flex flex-col">
        <button
          onClick={dismiss}
          className="absolute top-3 right-3 p-2 text-[#A3A3A3] hover:text-white"
          aria-label="Fechar"
          data-testid="daily-popup-dismiss"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-5 border-b border-[#222]">
          <div className="flex items-center gap-2 text-[#E50000]">
            <Zap className="w-4 h-4" />
            <div className="kw-overline">Reporte Rápido · Fim de Dia</div>
          </div>
          <h2 className="font-display text-2xl md:text-3xl font-black uppercase tracking-tight mt-1">
            Como correu o teu <span className="text-[#E50000]">{dayOffset === 0 ? "dia" : "ontem"}</span>?
          </h2>
          <p className="text-xs text-[#A3A3A3] mt-2">
            30 segundos para registar. Contribui já para o teu ranking semanal.
          </p>

          <div className="flex gap-2 mt-3" data-testid="daily-popup-day-toggle">
            <button
              onClick={() => setDayOffset(0)}
              className={`px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold border transition-colors ${
                dayOffset === 0
                  ? "border-[#E50000] bg-[#E50000] text-white"
                  : "border-[#222] text-[#A3A3A3] hover:text-white"
              }`}
              data-testid="daily-popup-today"
            >
              Hoje · {shortLabel(today)}
            </button>
            <button
              onClick={() => setDayOffset(-1)}
              className={`px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold border transition-colors ${
                dayOffset === -1
                  ? "border-[#E50000] bg-[#E50000] text-white"
                  : "border-[#222] text-[#A3A3A3] hover:text-white"
              }`}
              data-testid="daily-popup-yesterday"
            >
              Ontem · {shortLabel(yesterday)}
            </button>
          </div>
        </div>

        <div className="p-5 grid grid-cols-2 gap-3">
          {FIELDS.map((f) => (
            <div key={f.key} className="flex flex-col">
              <label className="text-[10px] uppercase tracking-widest text-[#737373] font-bold mb-1">
                {f.label}
              </label>
              <input
                type="number"
                min="0"
                inputMode="numeric"
                className="kw-input !text-center text-lg font-mono font-bold"
                value={values[f.key] ?? 0}
                onChange={(e) => setField(f.key, e.target.value)}
                data-testid={`daily-popup-${f.key}`}
              />
            </div>
          ))}
        </div>

        <div className="p-5 border-t border-[#222] flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
          <button
            onClick={dismiss}
            className="kw-btn-secondary inline-flex items-center justify-center gap-2"
            data-testid="daily-popup-later"
          >
            <Clock className="w-4 h-4" /> Lembrar mais tarde (4h)
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="kw-btn-primary inline-flex items-center justify-center gap-2"
            data-testid="daily-popup-save"
          >
            <CheckCircle2 className="w-4 h-4" /> {saving ? "A guardar…" : "Guardar reporte"}
          </button>
        </div>
      </div>
    </div>
  );
}
